import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../Login/SessionContext";
import { API_HOST } from "../../constants";
import { getCookie } from "../../utils/cookieUtil";
import { isAdminUser } from "../../utils/userUtils";
import Modal from "../ui/Modal";
import Button from "../ui/Button";

function Header() {
  const navigate = useNavigate();
  const { isLoggedIn, setIsLoggedIn, user, isLoading } = useSession();

  const [showPwChange, setShowPwChange] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleLogout = async () => {
    try {
      const csrfToken = getCookie("csrftoken");
      const response = await fetch(`${API_HOST}/api/logout/`, {
        method: "POST",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
      });

      if (response.ok) {
        setIsLoggedIn(false);
        setMenuOpen(false);
        navigate("/");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = search.trim();
    if (!query) return;
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <header className="relative z-50 border-b border-white/5 bg-ink-950/80 backdrop-blur">
      <div className="mx-auto flex w-full max-w-6xl flex-row flex-nowrap items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex shrink-0 items-center gap-4">
          <Link to="/" className="text-lg font-semibold text-white">
            <span className="font-display">썰모</span>
          </Link>
          <nav className="hidden items-center gap-3 text-sm text-ink-200 lg:flex">
            <Link to="/bbs/ssul" className="transition hover:text-white">
              썰
            </Link>
            <Link to="/bbs/ssulmanhwa" className="transition hover:text-white">
              썰만화
            </Link>
            <Link to="/bbs/ssulmovie" className="transition hover:text-white">
              썰영상
            </Link>
            <Link to="/tag" className="transition hover:text-white">
              태그
            </Link>
            {isAdminUser(user) && (
              <Link to="/admin" className="rounded-full border border-accent-400/40 bg-accent-400/10 px-3 py-1 text-xs font-semibold text-accent-100">
                관리자
              </Link>
            )}
          </nav>
        </div>

        <form onSubmit={handleSearchSubmit} className="min-w-0 flex-1">
          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 sm:px-4">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="게시글, 태그, 키워드 검색"
              className="w-full min-w-0 bg-transparent text-sm text-white placeholder:text-ink-200/60 focus:outline-none"
            />
          </div>
        </form>

        <div className="flex shrink-0 items-center justify-between gap-3 md:justify-end">
          {isLoading ? null : isLoggedIn ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white"
              >
                <span className="h-2 w-2 rounded-full bg-brand-400" />
                {user?.nickname || user?.username || "사용자"}
              </button>
              {menuOpen && (
                <div className="absolute right-0 z-50 mt-2 w-48 rounded-2xl border border-white/10 bg-ink-900/95 p-2 shadow-card">
                  <button
                    type="button"
                    className="w-full rounded-xl px-3 py-2 text-left text-sm text-ink-200 transition hover:bg-white/5 hover:text-white"
                    onClick={() => {
                      setShowPwChange(true);
                      setMenuOpen(false);
                    }}
                  >
                    비밀번호 변경
                  </button>
                  <button
                    type="button"
                    className="w-full rounded-xl px-3 py-2 text-left text-sm text-ink-200 transition hover:bg-white/5 hover:text-white"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/profile");
                    }}
                  >
                    프로필
                  </button>
                  <button
                    type="button"
                    className="w-full rounded-xl px-3 py-2 text-left text-sm text-red-200 transition hover:bg-red-500/10"
                    onClick={handleLogout}
                  >
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Button variant="secondary" onClick={() => navigate("/login")}>로그인</Button>
          )}
        </div>
      </div>

      <PasswordChangeModal
        open={showPwChange}
        onClose={() => setShowPwChange(false)}
        username={user?.username || ""}
      />
    </header>
  );
}

function PasswordChangeModal({ open, onClose, username }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!currentPassword) newErrors.currentPassword = "현재 비밀번호를 입력해야 합니다.";
    if (!password) newErrors.password = "비밀번호를 입력해야 합니다.";
    if (!confirmPassword) newErrors.confirmPassword = "비밀번호를 입력해야 합니다.";
    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = "입력한 비밀번호가 맞지 않습니다.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validateForm()) return;
    setLoading(true);

    const payload = {
      username,
      currentPassword,
      newPassword: password,
    };

    try {
      const csrfToken = getCookie("csrftoken");
      const response = await fetch(`${API_HOST}/api/password-change/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        onClose();
      } else {
        alert(data.error || "현재 비밀번호가 올바르지 않습니다.");
      }
    } catch (error) {
      alert("서버 오류가 발생했습니다. 다시 시도해주세요.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="비밀번호 변경"
      description="보안을 위해 주기적으로 비밀번호를 변경하세요."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button onClick={handleConfirm} disabled={loading}>
            {loading ? "변경 중..." : "확인"}
          </Button>
        </>
      }
    >
      <div>
        <label className="text-xs font-semibold text-ink-200">현재 비밀번호</label>
        <input
          type="password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          className="soft-input mt-2"
        />
        {errors.currentPassword && <p className="mt-1 text-xs text-red-200">{errors.currentPassword}</p>}
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-200">새 비밀번호</label>
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="soft-input mt-2"
        />
        {errors.password && <p className="mt-1 text-xs text-red-200">{errors.password}</p>}
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-200">새 비밀번호 확인</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          className="soft-input mt-2"
        />
        {errors.confirmPassword && <p className="mt-1 text-xs text-red-200">{errors.confirmPassword}</p>}
      </div>
    </Modal>
  );
}

export default Header;
